import React, { useEffect, useRef, useState } from 'react'
import { Nav } from 'react-bootstrap'
import './styles/index.scss'
import Icon from 'icons/Icon'
import { ICON_VARIANT, TAB_ICON_VARIANTS_BY_NAME } from './constants'

/**
 * A reusable, tenant-friendly tabs view built on top of React-Bootstrap Nav.
 * Follows BEM block naming convention with .tabs-view
 *
 * @param {Object[]} items - Array of tab items
 * @param {string} items[].key - Unique identifier for the tab
 * @param {string} items[].label - Display text for the tab
 * @param {string} [items[].icon] - Optional icon name to display
 * @param {('outline'|'filled')} [items[].iconVariant] - Icon style: outline (stroke only) or filled (stroke + fill). Defaults from TAB_ICON_VARIANTS_BY_NAME or 'outline'
 * @param {string} [items[].imageUrl] - Optional image URL
 * @param {boolean} [items[].disabled] - Whether the tab is disabled
 * @param {Function} onSelect - Callback fired when tab is selected: (key) => void
 * @param {string} [activeKey] - Key of currently active tab (controlled mode)
 * @param {string} [defaultActiveKey] - Initial active tab key (uncontrolled mode)
 * @param {('pills'|'tabs')} [variant='pills'] - Visual style variant
 * @param {('sm'|'md'|'lg')} [size='md'] - Size variant
 * @param {boolean} [fill=false] - Whether tabs should take equal width
 * @param {string} [className=''] - Additional CSS classes to apply
 *
 * @returns {JSX.Element} Tabs view component
 */
const TabsView = ({
  items = [],
  onSelect,
  activeKey,
  defaultActiveKey,
  variant = 'pills',
  size = 'md',
  fill = false,
  className = ''
}) => {
  // check if the tabs are overflowing and add a scrollable class if they are
  const navRef = useRef(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [showLeftShadow, setShowLeftShadow] = useState(false)
  const [showRightShadow, setShowRightShadow] = useState(false)

  useEffect(() => {
    if (!navRef?.current) return

    const checkOverflow = () => {
      const el = navRef.current
      setIsOverflowing(el?.scrollWidth > el?.clientWidth)
    }

    checkOverflow()

    // eslint-disable-next-line no-undef
    const observer = new ResizeObserver(checkOverflow)
    observer.observe(navRef?.current)

    window.addEventListener('resize', checkOverflow)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', checkOverflow)
    }
  }, [items])

  useEffect(() => {
    if (!navRef?.current) return

    const handleScroll = () => {
      const el = navRef.current
      if (!el) return

      const scrollLeft = el.scrollLeft
      const maxScrollLeft = el.scrollWidth - el.clientWidth

      // Show left shadow when scrolled away from the left edge (threshold of 5px)
      setShowLeftShadow(scrollLeft > 5)

      // Show right shadow when not at the rightmost position (threshold of 5px)
      setShowRightShadow(scrollLeft < maxScrollLeft - 5)
    }

    // Initial check
    handleScroll()

    const el = navRef.current
    el.addEventListener('scroll', handleScroll)

    return () => {
      el.removeEventListener('scroll', handleScroll)
    }
  }, [isOverflowing])

  const wrapClasses = [
    'tabs-view',
    `tabs-view--${size}`,
    fill ? 'tabs-view--fill' : '',
    isOverflowing ? 'tabs-view--scrollable' : '',
    showLeftShadow ? 'tabs-view--shadow-left' : '',
    showRightShadow ? 'tabs-view--shadow-right' : '',
    className
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={wrapClasses}>
      <Nav
        ref={navRef}
        variant={variant}
        onSelect={onSelect}
        activeKey={activeKey}
        defaultActiveKey={defaultActiveKey}
        className='tabs-view__list nav-pills'
        role='tablist'
      >
        {items?.map((item) => (
          <Nav.Item key={`tab-${item.key}`} className='tabs-view__item' role='presentation'>
            <Nav.Link
              eventKey={item.key}
              className='tabs-view__link'
              disabled={item.disabled}
            >
              {item.icon
                ? (
                  <span
                    className={`tabs-view__icon tabs-view__icon--${item.iconVariant ?? TAB_ICON_VARIANTS_BY_NAME[item.icon] ?? ICON_VARIANT.FILLED}`}
                  >
                    <Icon name={item.icon} width='20px' height='20px' />
                  </span>
                  )
                : null}
              {item.imageUrl ? <img src={item.imageUrl} alt={item.label} className='tabs-view__image' /> : null}
              <span className='tabs-view__label'>{item.label}</span>
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
    </div>
  )
}

export default TabsView
